<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Check } from 'lucide-vue-next';
import { authAPI } from '../services/api';
import { useAuthState } from '../composables/useAuthState';

const router = useRouter();
const { t } = useI18n();
const { getPersistedEmail, getPersistedData, clearPersistedData } = useAuthState();

const email = ref('');
const activationCode = ref('');
const isLoading = ref(false);
const error = ref<string | null>(null);
const showEmailInput = ref(false);

const isStepValid = computed(() => {
  if (showEmailInput.value) {
    return email.value && activationCode.value.length >= 4;
  }
  return activationCode.value.length >= 4;
});

const getButtonText = () => {
  if (isLoading.value) return t('activation.loading');
  return t('activation.submit');
};

onMounted(() => {
  const persistedEmail = getPersistedEmail();
  const persistedData = getPersistedData();

  if (persistedEmail) {
    email.value = persistedEmail;
    showEmailInput.value = false;
  } else {
    showEmailInput.value = true;
  }

  if (persistedData) {
    // Pre-fill other data if needed in the future
  }
});

const handleActivate = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    const response = await authAPI.activate({
      email: email.value,
      code: activationCode.value,
    });

    // Clear persisted registration data
    clearPersistedData();

    // Save token if provided
    if (response.token) {
      localStorage.setItem('token', response.token);
    }

    // Check if setup is required
    if (response.requires_setup && response.setup_token) {
      // Store setup token and redirect to complete setup
      localStorage.setItem('setup_token', response.setup_token);
      router.push('/auth/complete-setup');
    } else {
      router.push('/messenger');
    }
  } catch (err: any) {
    error.value = err.response?.data?.message || t('activation.error_generic');
    console.error('Activation error:', err);
  } finally {
    isLoading.value = false;
  }
};

const handleSubmit = (e: Event) => {
  e.preventDefault();
  handleActivate();
};
</script>

<template>
  <div class="min-h-screen bg-[#18181C] flex items-center justify-center p-6">
    <div class="w-full max-w-md">
      <div class="bg-[#111113] rounded-3xl shadow-sm border border-[#2A2018] p-8 md:p-10">
        <!-- Header -->
        <div class="mb-8 text-center">
          <div
            class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <Check class="w-8 h-8 text-green-600" />
          </div>
          <h1 class="text-2xl text-[#F5F0EA] mb-2">{{ $t('activation.title') }}</h1>
          <p class="text-[#9E9080]">{{ $t('activation.subtitle') }}</p>
        </div>

        <!-- Form -->
        <form @submit="handleSubmit">
          <div class="space-y-5">
            <div v-if="showEmailInput" class="space-y-4">
              <div>
                <label for="email" class="block text-sm text-[#F5F0EA] mb-2">
                  {{ $t('activation.email_label') }}
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  v-model="email"
                  class="w-full px-4 py-3 bg-[#18181C] border border-[#2A2018] rounded-2xl focus:bg-[#111113] focus:ring-2 focus:ring-[#D4956A] focus:border-transparent outline-none transition text-[#F5F0EA] placeholder:text-[#524840]"
                  :placeholder="$t('activation.email_placeholder')"
                />
                <p class="mt-2 text-xs text-[#524840]">{{ $t('activation.email_hint') }}</p>
              </div>
            </div>

            <div v-else class="text-center mb-4">
              <p class="text-[#9E9080]">
                {{ $t('activation.sent_to') }}
                <strong class="text-[#F5F0EA]">{{ email }}</strong>
              </p>
            </div>

            <div>
              <label for="activationCode" class="block text-sm text-[#F5F0EA] mb-2">
                {{ $t('activation.code_label') }}
              </label>
              <input
                type="text"
                id="activationCode"
                required
                maxlength="6"
                v-model="activationCode"
                @input="activationCode = activationCode.replace(/[^0-9]/g, '')"
                class="w-full px-4 py-3 bg-[#18181C] border border-[#2A2018] rounded-2xl focus:bg-[#111113] focus:ring-2 focus:ring-[#D4956A] focus:border-transparent outline-none transition text-[#F5F0EA] placeholder:text-[#524840] text-center tracking-widest text-lg"
                placeholder="000000"
              />
            </div>

            <div
              v-if="error"
              class="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"
            >
              {{ error }}
            </div>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="!isStepValid || isLoading"
            class="w-full flex items-center justify-center gap-2 px-5 py-3 mt-8 bg-[#0A0A0B] text-[#F5F0EA] rounded-2xl hover:bg-[#111113] disabled:bg-[#1E1E23] disabled:text-[#524840] disabled:cursor-not-allowed transition"
          >
            <span
              v-if="isLoading"
              class="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
            ></span>
            {{ getButtonText() }}
          </button>
        </form>

        <!-- Back to Login Link -->
        <p class="mt-8 text-center text-sm text-[#9E9080]">
          {{ $t('activation.already_have_account') }}
          <router-link to="/login" class="text-[#F5F0EA] hover:underline">
            {{ $t('activation.sign_in') }}
          </router-link>
        </p>
      </div>
    </div>
  </div>
</template>
